<template>
  <el-dialog 
    :visible="visible" 
    @close="handleClose"
    title="頻率設定"
    width="800px"
  >
    <div class="panel-header">
      <div class="info-row">
        <span class="label">儀器名稱：</span>
        <span class="value">{{ instrumentData.name }}</span>
      </div>
      <div class="info-row">
        <span class="label">初值日期：</span>
        <span class="value">{{ formatDate(instrumentData.startDate) }}</span>
      </div>
    </div>

    <div class="settings-section">
      <div class="section-header">
        <h3>上傳頻率設定</h3>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="addNewSetting"
        >
          新增設定
        </el-button>
      </div>

      <el-table
        :data="localSettingData"
        border
        style="width: 100%; margin-top: 16px"
        :row-class-name="getRowClassName"
      >
        <!-- 設定類型 -->
        <el-table-column prop="settingType" label="設定類型" width="120">
          <template slot-scope="{ row }">
            <el-select 
              v-model="row.settingType" 
              placeholder="請選擇類型" 
              size="small"
              style="width: 100%"
            >
              <el-option label="測試" value="test"></el-option>
              <el-option label="正常" value="normal"></el-option>
              <el-option label="維護" value="maintenance"></el-option>
              <el-option label="停用" value="disabled"></el-option>
            </el-select>
          </template>
        </el-table-column>

        <!-- 開始時間 -->
        <el-table-column prop="start_date" label="開始時間" width="160">
          <template slot-scope="{ row, $index }">
            <div :class="{ 'date-error': hasOverlap($index) }">
              <el-date-picker
                v-model="row.start_date"
                type="date"
                placeholder="選擇開始時間"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                size="small"
                style="width: 100%"
                @change="checkOverlap"
              />
              <span v-if="hasOverlap($index)" class="error-icon">
                <i class="el-icon-warning"></i>
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- 結束時間 -->
        <el-table-column prop="end_date" label="結束時間" width="160">
          <template slot-scope="{ row, $index }">
            <div :class="{ 'date-error': hasOverlap($index) }">
              <el-date-picker
                v-model="row.end_date"
                type="date"
                placeholder="選擇結束時間"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                size="small"
                style="width: 100%"
                :picker-options="getEndDatePickerOptions(row)"
                @change="checkOverlap"
              />
              <span v-if="hasOverlap($index)" class="error-icon">
                <i class="el-icon-warning"></i>
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- 檢查頻率 -->
        <el-table-column prop="frequency" label="檢查頻率" width="120">
          <template slot-scope="{ row }">
            <el-input-number
              v-model="row.frequency"
              :min="1"
              :max="999"
              placeholder="頻率"
              controls-position="right"
              size="small"
              style="width: 100%"
            />
          </template>
        </el-table-column>

        <!-- 頻率單位 -->
        <el-table-column prop="frequency_unit" label="頻率單位" width="120">
          <template slot-scope="{ row }">
            <el-select 
              v-model="row.frequency_unit" 
              placeholder="單位" 
              size="small"
              style="width: 100%"
            >
              <el-option label="週" value="week"></el-option>
              <el-option label="月" value="month"></el-option>
            </el-select>
          </template>
        </el-table-column>

        <!-- 操作 -->
        <el-table-column label="操作" width="60" align="center" fixed="right">
          <template slot-scope="{ $index }">
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="deleteSetting($index)"
              circle
            />
          </template>
        </el-table-column>
      </el-table>

      <el-empty
        v-if="localSettingData.length === 0"
        description="尚無設定資料"
        :image-size="100"
      />
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="saveSettings">儲存</el-button>
    </span>
    <!-- 重疊提示訊息 -->
    <el-alert
      v-if="overlapIndexes.length > 0"
      type="warning"
      :closable="false"
      style="margin-top: 12px"
    >
      <template slot="title">
        <i class="el-icon-warning"></i>
        偵測到時間重疊：
        <span v-for="(pair, index) in getOverlapPairs()" :key="index">
          第 {{ pair.index1 + 1 }} 筆與第 {{ pair.index2 + 1 }} 筆
          <span v-if="index < getOverlapPairs().length - 1">、</span>
        </span>
      </template>
    </el-alert>
  </el-dialog>
</template>

<script>
import moment from 'moment';
export default {
  name: 'FrequencySettingDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    instrumentData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      localSettingData: [],
      overlapIndexes: [],  // 儲存有重疊的索引

      closePass: false,
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Dialog 開啟時，初始化資料
        //console.log('visible');
        this.initSettingData();
      }
    },
    // 監聽資料變化，即時檢查重疊
    localSettingData: {
      handler() {
        this.checkOverlap();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    initSettingData() {
      const timeoutSetting = this.instrumentData.timeoutSetting || [];
      this.localSettingData = JSON.parse(JSON.stringify(timeoutSetting));
      this.closePass = false;
    },

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('YYYY-MM-DD HH:mm');
    },

    addNewSetting() {
      let count = this.localSettingData.length;
      let last_date = count>0 ? this.localSettingData[count-1].end_date : this.instrumentData.startDate;

      const newSetting = {
        settingType: 'normal',
        start_date: new Date(last_date).toISOString(),
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        frequency: 1,
        frequency_unit: 'week',
      };
      
      this.localSettingData.push(newSetting);
    },

    deleteSetting(index) {
      this.$confirm('確定要刪除此設定嗎？', '提示', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.localSettingData.splice(index, 1);
          this.$message.success('刪除成功');
        })
        .catch((e) => {
          console.error(e);
        });
    },

    // 驗證設定
    validateSettings() {
      const settings = this.localSettingData;

      for (let i = 0; i < settings.length; i++) {
        const setting = settings[i];

        // 檢查必填欄位
        if (!setting.settingType) {
          this.$message.error(`第 ${i + 1} 筆設定：請選擇設定類型`);
          return false;
        }

        if (!setting.start_date) {
          this.$message.error(`第 ${i + 1} 筆設定：請選擇開始時間`);
          return false;
        }

        if (!setting.end_date) {
          this.$message.error(`第 ${i + 1} 筆設定：請選擇結束時間`);
          return false;
        }

        // 檢查頻率
        if (!setting.frequency || setting.frequency < 1) {
          this.$message.error(`第 ${i + 1} 筆設定：請輸入有效的檢查頻率`);
          return false;
        }

        if (!setting.frequency_unit) {
          this.$message.error(`第 ${i + 1} 筆設定：請選擇頻率單位`);
          return false;
        }

        // 檢查時間邏輯
        const startDate = moment(setting.start_date);
        const endDate = moment(setting.end_date);

        if (!startDate.isValid()) {
          this.$message.error(`第 ${i + 1} 筆設定：開始時間格式錯誤`);
          return false;
        }

        if (!endDate.isValid()) {
          this.$message.error(`第 ${i + 1} 筆設定：結束時間格式錯誤`);
          return false;
        }

        if (endDate.isBefore(startDate)) {
          this.$message.error(`第 ${i + 1} 筆設定：結束時間必須大於開始時間`);
          return false;
        }

        // 檢查是否有重疊
        if (this.overlapIndexes.length > 0) {
          this.$message.error('存在時間重疊的設定，請修正後再儲存');
          return false;
        }
      }

      return true;
    },

    async saveSettings() {
      if (!this.validateSettings()) return;

      this.closePass = true;
      this.$emit('save', {
        instrumentId: this.instrumentData._id,
        instrumentName: this.instrumentData.name,
        settingData: this.localSettingData,
      });
    },

    handleClose() {
      if (this.hasUnsavedChanges()) {
        this.$confirm('確定要關閉嗎？未儲存的變更將會遺失', '提示', {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => {
            this.closePass = true;
            this.$emit('update:visible', false);
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        this.closePass = true;
        this.$emit('update:visible', false);
      }
    },

    hasUnsavedChanges() {
      const original = JSON.stringify(this.instrumentData.timeoutSetting || []);
      const current = JSON.stringify(this.localSettingData);
      return (!this.closePass) && original !== current;
    },

    // 結束時間選擇器選項（限制不能早於開始時間）
    getEndDatePickerOptions(row) {
      return {
        disabledDate(date) {
          if (!row.start_date) return false;
          return date < new Date(row.start_date);
        },
      };
    },

    // 檢查所有時間重疊
    checkOverlap() {
      const overlaps = new Set();
      const settings = this.localSettingData;

      for (let i = 0; i < settings.length; i++) {
        const setting1 = settings[i];
        
        // 跳過沒有完整日期的項目
        if (!setting1.start_date || !setting1.end_date) continue;

        const start1 = moment(setting1.start_date);
        const end1 = moment(setting1.end_date);

        // 檢查日期有效性
        if (!start1.isValid() || !end1.isValid()) continue;

        for (let j = i + 1; j < settings.length; j++) {
          const setting2 = settings[j];
          
          // 跳過沒有完整日期的項目
          if (!setting2.start_date || !setting2.end_date) continue;

          const start2 = moment(setting2.start_date);
          const end2 = moment(setting2.end_date);

          // 檢查日期有效性
          if (!start2.isValid() || !end2.isValid()) continue;

          // 檢查時間區間是否重疊
          // 重疊條件：start1 <= end2 && end1 >= start2
          if (this.isDateRangeOverlap(start1, end1, start2, end2)) {
            overlaps.add(i);
            overlaps.add(j);
          }
        }
      }

      this.overlapIndexes = Array.from(overlaps);
    },

    // 判斷兩個日期區間是否重疊
    isDateRangeOverlap(start1, end1, start2, end2) {
      return start1.isBefore(end2) && end1.isAfter(start2);
    },

    // 檢查特定索引是否有重疊
    hasOverlap(index) {
      return this.overlapIndexes.includes(index);
    },
    // 取得重疊的配對
    getOverlapPairs() {
      const pairs = [];
      const settings = this.localSettingData;

      for (let i = 0; i < settings.length; i++) {
        if (!this.overlapIndexes.includes(i)) continue;

        const setting1 = settings[i];
        if (!setting1.start_date || !setting1.end_date) continue;

        const start1 = moment(setting1.start_date);
        const end1 = moment(setting1.end_date);

        for (let j = i + 1; j < settings.length; j++) {
          if (!this.overlapIndexes.includes(j)) continue;

          const setting2 = settings[j];
          if (!setting2.start_date || !setting2.end_date) continue;

          const start2 = moment(setting2.start_date);
          const end2 = moment(setting2.end_date);

          if (this.isDateRangeOverlap(start1, end1, start2, end2)) {
            pairs.push({ index1: i, index2: j });
          }
        }
      }

      return pairs;
    },
    // 設定列的 class name
    getRowClassName({ row, rowIndex }) {
      if (this.overlapIndexes.includes(rowIndex)) {
        return 'row-warning';
      }
      return '';
    },
  },
};
</script>

<style scoped>
.panel-header {
  background-color: #f5f7fa;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 600;
  color: #606266;
  min-width: 100px;
}

.value {
  color: #303133;
}

.settings-section {
  margin-top: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 重疊警告的列 */
:deep(.el-table .row-warning) {
  background-color: #fef0f0;
}

:deep(.el-table .row-warning:hover > td) {
  background-color: #fde2e2 !important;
}

/* 日期欄位錯誤樣式 */
.date-error {
  position: relative;
}

.date-error :deep(.el-input__inner) {
  border-color: #f56c6c;
  background-color: #fef0f0;
}

.date-error :deep(.el-input__inner:focus) {
  border-color: #f56c6c;
}

/* 錯誤圖示 */
.error-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #f56c6c;
  font-size: 16px;
  pointer-events: none;
  z-index: 1;
}

/* Alert 樣式調整 */
:deep(.el-alert--warning) {
  background-color: #fdf6ec;
  border-color: #f5dab1;
}

:deep(.el-alert__title) {
  font-size: 13px;
  line-height: 1.6;
}
</style>